import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ConsultationProvider } from "./contexts/ConsultationContext";
import LoginPage from "./pages/LoginPage";
import StaffLoginPage from "./pages/StaffLoginPage";
// Route-level code splitting: each screen downloads only when first opened.
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const PatientProfilePage = lazy(() => import("./pages/PatientProfilePage"));
const IPProfilePage = lazy(() => import("./pages/IPProfilePage"));
const WardStationPage = lazy(() => import("./pages/WardStationPage"));
const HandoverSheetPage = lazy(() => import("./pages/HandoverSheetPage"));
const TumorBoardPage = lazy(() => import("./pages/TumorBoardPage"));
const TumorBoardNoteFormPage = lazy(() => import("./pages/TumorBoardNoteFormPage"));
const TumorBoardNoteDetailPage = lazy(() => import("./pages/TumorBoardNoteDetailPage"));
const PurchaseModule = lazy(() => import("./pages/PurchaseModule"));
const PharmacyPage = lazy(() => import("./pages/PharmacyPage"));
const PharmacyStoresPage = lazy(() => import("./pages/PharmacyStoresPage"));
const StoresAdminPage = lazy(() => import("./pages/StoresAdminPage"));
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";
import { installAuthInterceptor } from "./utils/axiosAuth";

// Install the global 401 → /auth/refresh → retry interceptor before any
// component fires its first request. Idempotent inside the module so HMR
// doesn't double-register.
installAuthInterceptor();

// Map a user's role to the landing page they should see by default.
function homeForRole(role) {
  if (role === 'pharmacy') return '/pharmacy';
  if (role === 'purchase') return '/purchase';
  if (role === 'pharmacystores') return '/pharmacy-stores';
  if (role === 'storesadmin') return '/stores-admin';
  return '/dashboard';
}

// Generic protected route — every signed-in user can pass through.
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-700 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }
  // Restrict by role when the route specifies a list. Out-of-role users get
  // bounced to *their* home page (not 404) so the URL bar always reflects an
  // accessible screen.
  if (allowedRoles && !allowedRoles.includes(user.role || 'doctor')) {
    return <Navigate to={homeForRole(user.role)} replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-700 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (user && user !== false) {
    return <Navigate to={homeForRole(user.role)} replace />;
  }
  return children;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    const suffix =
      p.startsWith('/login')         ? 'Sign In' :
      p.startsWith('/staff/login')   ? 'Staff Sign In' :
      p.startsWith('/pharmacy/login')? 'Pharmacy Sign In' :
      p.startsWith('/purchase/login')? 'Purchase Sign In' :
      p.startsWith('/dashboard')     ? 'Dashboard' :
      p.startsWith('/pharmacy-stores')? 'Pharmacy Stores' :
      p.startsWith('/stores-admin')  ? 'Stores Admin' :
      p.startsWith('/purchase')      ? 'Purchase' :
      p.startsWith('/pharmacy')      ? 'Pharmacy' :
      p.startsWith('/consultation')  ? 'Consultation' :
      '';
    document.title = suffix ? `EMR | eCare360 · ${suffix}` : 'EMR | eCare360';
  }, [location.pathname]);
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-[#F4F6F5]"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>}>
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/staff/login" element={<PublicRoute><StaffLoginPage /></PublicRoute>} />
      <Route path="/pharmacy/login" element={<PublicRoute><StaffLoginPage role="pharmacy" /></PublicRoute>} />
      <Route path="/purchase/login" element={<PublicRoute><StaffLoginPage role="purchase" /></PublicRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={["doctor"]}><DashboardPage /></ProtectedRoute>} />
      <Route path="/patient/:patientId" element={<ProtectedRoute allowedRoles={["doctor"]}><PatientProfilePage /></ProtectedRoute>} />
      <Route path="/ip/*" element={<ProtectedRoute><IPProfilePage /></ProtectedRoute>} />
      <Route path="/ward" element={<ProtectedRoute><WardStationPage /></ProtectedRoute>} />
      <Route path="/ward/handover-sheet" element={<ProtectedRoute><HandoverSheetPage /></ProtectedRoute>} />
      <Route path="/tumor-board" element={<ProtectedRoute allowedRoles={["doctor"]}><TumorBoardPage /></ProtectedRoute>} />
      <Route path="/tumor-board/patient/:patientId/note/new" element={<ProtectedRoute allowedRoles={["doctor"]}><TumorBoardNoteFormPage /></ProtectedRoute>} />
      <Route path="/tumor-board/note/:noteId" element={<ProtectedRoute allowedRoles={["doctor"]}><TumorBoardNoteDetailPage /></ProtectedRoute>} />
      <Route path="/tumor-board/note/:noteId/edit" element={<ProtectedRoute allowedRoles={["doctor"]}><TumorBoardNoteFormPage /></ProtectedRoute>} />
      <Route path="/pharmacy" element={<ProtectedRoute allowedRoles={["pharmacy"]}><PharmacyPage /></ProtectedRoute>} />
      <Route path="/purchase" element={<ProtectedRoute allowedRoles={["purchase"]}><PurchaseModule /></ProtectedRoute>} />
      <Route path="/pharmacy-stores" element={<ProtectedRoute allowedRoles={["pharmacystores"]}><PharmacyStoresPage /></ProtectedRoute>} />
      <Route path="/stores-admin" element={<ProtectedRoute allowedRoles={["storesadmin"]}><StoresAdminPage /></ProtectedRoute>} />
      <Route path="/" element={<RoleHomeRedirect />} />
      <Route path="*" element={<RoleHomeRedirect />} />
    </Routes>
    </Suspense>
  );
}

// "/" and unknown paths route by role so a Pharmacy user landing on a
// stale doctor URL ends up where they belong.
function RoleHomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeForRole(user.role)} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
